import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Heart, CreditCard, Smartphone, Loader2, CheckCircle2, Download, ShieldCheck } from "lucide-react";

const AMOUNTS = [1, 2, 5, 10, 20];
const METHODS = [
  { id: "card", label: "Carte bancaire", icon: CreditCard },
  { id: "mpesa", label: "M-Pesa", icon: Smartphone },
  { id: "airtel", label: "Airtel Money", icon: Smartphone },
  { id: "orange", label: "Orange Money", icon: Smartphone },
];

export default function SupportModal({ open, onOpenChange, media }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState("");
  const [method, setMethod] = useState("card");
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState("form"); // form | processing | done
  const [ref, setRef] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");

  const finalAmount = custom ? parseFloat(custom) : amount;

  const submit = async () => {
    if (!user) { toast.error("Connectez-vous pour soutenir un média."); navigate("/login"); return; }
    if (!finalAmount || finalAmount <= 0) { toast.error("Montant invalide"); return; }
    if (method !== "card" && !phone.trim()) { toast.error("Entrez votre numéro Mobile Money"); return; }
    setStep("processing");
    try {
      const { data } = await api.post(`/media/${media.id}/support`, {
        amount: finalAmount, currency: "USD", method, phone,
        origin_url: window.location.origin,
      });
      setRef(data.reference);
      if (data.requires_redirect && data.checkout_url) {
        // Card payment via Stripe Checkout — redirect to the hosted payment page.
        toast.info("Redirection vers le paiement sécurisé…");
        sessionStorage.setItem("support_return", JSON.stringify({ mediaSlug: media.slug, type: media.type }));
        window.location.href = data.checkout_url;
        return;
      }
      if (data.requires_polling) {
        // Real Mobile Money via PawaPay — the server marks the support "completed"
        // as soon as the deposit-callback (webhook) is received → confirmation instantanée.
        toast.info("Confirmez le paiement sur votre téléphone…");
        setStatusMsg("En attente de confirmation Mobile Money…");
        const start = Date.now();
        let ticks = 0;
        const poll = async () => {
          ticks += 1;
          try {
            // Fast path: read the server status updated in real time by the PawaPay callback.
            const { data: ss } = await api.get(`/payments/status/${data.reference}`);
            if (ss.status === "completed") { setStatusMsg(""); setStep("done"); return; }
            if (ss.status === "failed") { toast.error("Paiement échoué ou annulé."); setStatusMsg(""); setStep("form"); return; }
            // Backstop every ~10s: poll PawaPay directly in case webhooks are not configured.
            if (ticks % 4 === 0) {
              const { data: st } = await api.post(`/payments/${data.reference}/refresh`);
              if (st.status === "completed") { setStatusMsg(""); setStep("done"); return; }
              if (st.status === "failed") { toast.error("Paiement échoué ou annulé."); setStatusMsg(""); setStep("form"); return; }
            }
          } catch {}
          if (Date.now() - start > 120000) { toast.error("Délai dépassé. Vérifiez votre téléphone puis réessayez."); setStatusMsg(""); setStep("form"); return; }
          setTimeout(poll, 2500);
        };
        setTimeout(poll, 2500);
      } else {
        // Simulated provider confirmation (stands in for a real webhook)
        await new Promise((r) => setTimeout(r, 1400));
        await api.post(`/payments/${data.reference}/confirm`);
        setStep("done");
      }
    } catch (e) {
      toast.error(apiErr(e));
      setStep("form");
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setStep("form"); setRef(null); setCustom(""); setAmount(5); setStatusMsg(""); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent className="max-w-md" data-testid="support-modal">
        {!media?.certified ? (
          <div className="text-center py-6" data-testid="support-not-certified">
            <ShieldCheck className="mx-auto text-rdcgold mb-3" size={56} />
            <DialogTitle className="font-heading text-xl">Certification requise</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              <b>{media?.name}</b> n'est pas encore certifiée. Seules les chaînes officiellement certifiées peuvent recevoir des soutiens financiers, afin de protéger les spectateurs.
            </p>
            <p className="text-xs text-muted-foreground mt-3">
              Le gestionnaire de la chaîne peut demander la certification depuis son tableau de bord (Certification). Une fois validée par l'administration, les soutiens seront activés.
            </p>
            <Button onClick={close} className="mt-5 w-full" data-testid="support-not-certified-close">Compris</Button>
          </div>
        ) : step === "done" ? (
          <div className="text-center py-6">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={56} />
            <DialogTitle className="font-heading text-xl">Merci pour votre soutien !</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Votre soutien de <b>{finalAmount} USD</b> à <b>{media.name}</b> a été confirmé.
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 font-mono">Réf: {ref}</p>
            <a
              href={`${API}/supports/${ref}/receipt`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="support-receipt-btn"
              className="mt-4 inline-flex items-center justify-center gap-2 w-full h-11 rounded-md border border-border bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-border transition-colors"
            >
              <Download size={16} /> Télécharger le reçu (PDF)
            </a>
            <p className="text-[11px] text-muted-foreground mt-2">Un reçu a aussi été envoyé à votre adresse email.</p>
            <Button onClick={close} className="mt-3 w-full" data-testid="support-done-btn">Fermer</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Heart className="text-rdcred" size={20} /> Soutenir {media.name}
              </DialogTitle>
              <DialogDescription>
                Aidez ce média congolais à continuer de produire du contenu de qualité.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-2 mt-2">
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  data-testid={`donation-amount-${a}-usd`}
                  onClick={() => { setAmount(a); setCustom(""); }}
                  className={`rounded-xl border-2 py-3 font-heading font-bold transition-all ${
                    !custom && amount === a ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"
                  }`}
                >
                  ${a}
                </button>
              ))}
              <Input
                type="number"
                placeholder="Autre"
                value={custom}
                data-testid="donation-custom-input"
                onChange={(e) => setCustom(e.target.value)}
                className="text-center font-heading font-bold h-full"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 mt-2">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`payment-method-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm font-medium transition-all ${
                    method === m.id ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"
                  }`}
                >
                  <m.icon size={16} /> {m.label}
                </button>
              ))}
            </div>

            {method !== "card" && (
              <Input
                placeholder="Numéro Mobile Money (+243...)"
                value={phone}
                data-testid="donation-phone-input"
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1"
              />
            )}

            <p className="text-[11px] text-muted-foreground text-center bg-secondary rounded-lg py-2">
              Paiement sécurisé. Le Mobile Money réel s'active dès que l'administration configure le prestataire ; sinon le paiement est simulé (aucune vraie transaction).
            </p>

            {step === "processing" && statusMsg && (
              <div className="flex items-center justify-center gap-2 rounded-lg bg-primary/10 text-primary py-2.5 text-sm font-medium" data-testid="support-status-live">
                <Loader2 className="animate-spin" size={16} /> {statusMsg}
              </div>
            )}

            <Button onClick={submit} disabled={step === "processing"} className="w-full h-12 text-base font-heading font-bold" data-testid="support-submit-btn">
              {step === "processing" ? (
                <><Loader2 className="animate-spin mr-2" size={18} /> Traitement…</>
              ) : (
                <><Heart size={18} className="mr-2" /> Soutenir avec ${finalAmount || 0}</>
              )}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
